/* eslint-disable no-plusplus,react/jsx-pascal-case */
import PropTypes from 'prop-types';
import React from 'react';
import { getExperimentID } from 'ctm-app/client/store/selectors';
import { pageContext } from 'ctm-util-functions';
import { useSelector } from 'react-redux';
import { SimpleFlexbox } from '../../../../flexbox/flexbox';
import { experimentIDEnum } from '../../../../../../ctm-vertical-base/base/reducers/page';

/**
 * Main layout component for rendering a unavailable results row
 * @param {Object} props - The components props
 * @param {Array} props.xs - componentLists for xs breakpoint and above
 * @param {Array} props.md - componentLists for sm breakpoint and above
 * @param {Object} props.stateProps - The stateful product object passed in
 * @returns {Node} The row layout component
 * @constructor
 */
const LayoutUnavailableResultsRow = ({ xs, md, stateProps }) => {
    const breakpoint = pageContext.getProps().pageState.breakpoint;
    const experimentID = useSelector(getExperimentID);

    switch (breakpoint) {
        case 'xs':
        case 'sm':
            return <_Xs componentLists={xs} stateProps={stateProps} />;
        default: {
            // MD to XXL breakpoint
            return <_Md componentLists={md} stateProps={stateProps} experimentID={experimentID} />;
        }
    }
};

LayoutUnavailableResultsRow.propTypes = {
    xs: PropTypes.arrayOf(PropTypes.array),
    md: PropTypes.arrayOf(PropTypes.array),
    stateProps: PropTypes.object
};

/**
 * Private component to render layout for XS breakpoint
 * @param {Object} props - The components props
 * @param {Array} props.componentLists - all sub component lists to be rendered under this layout
 * @param {Object} props.stateProps - The stateful product object passed in
 * @returns {_Xs} Xs layout component
 * @private
 */
const _Xs = ({ componentLists, stateProps }) => {
    let i = 0;
    const { constructComponents } = pageContext.getProps().director;
    return (
        <SimpleFlexbox display="flex" wrap itemsCenter="xs" margin="top" padding="top_bottom" border="all" bgColor="brand_base_0">
            <SimpleFlexbox xs={4} sm={2} padding="left_right">
                {constructComponents(componentLists[i++], { stateProps })}
            </SimpleFlexbox>
            <SimpleFlexbox column flex={1} display="flex" padding="left_right">
                {constructComponents(componentLists[i], { stateProps })}
            </SimpleFlexbox>
        </SimpleFlexbox>
    );
};

_Xs.propTypes = {
    componentLists: PropTypes.arrayOf(PropTypes.array),
    stateProps: PropTypes.object
};

/**
 * Private component to render layout for MD breakpoint and up
 * @param {Object} props - The components props
 * @param {Array} props.componentLists - all sub component lists to be rendered under this layout
 * @param {Object} props.stateProps - The stateful product object passed in
 * @param {String} props.experimentID - The experiment ID from state
 * @returns {_Md} Md layout component
 * @private
 */
const _Md = ({ componentLists, stateProps, experimentID }) => {
    let i = 0;
    const { director: { constructComponents }, pageState: { breakpoint } } = pageContext.getProps();

    // For CTM_REBRAND experiment, use xl sizing (10) instead of xxl sizing (12)
    let xlSize = breakpoint === 'xxl' ? 12 : 10;
    if (experimentID === experimentIDEnum.CTM_REBRAND && breakpoint === 'xxl') {
        xlSize = 10;
    }

    return (
        <SimpleFlexbox md={9} xl={xlSize} display="flex" wrap itemsCenter="xs" margin="top" border="all" bgColor="brand_base_0">
            <SimpleFlexbox md={2} padding="left_right">
                {constructComponents(componentLists[i++], { stateProps })}
            </SimpleFlexbox>
            <SimpleFlexbox md={6} lg={5} padding="left_right">
                {constructComponents(componentLists[i++], { stateProps })}
            </SimpleFlexbox>
            <SimpleFlexbox md={4} lg={5} padding="left_right" variant="flex_item_align_right">
                {constructComponents(componentLists[i], { stateProps })}
            </SimpleFlexbox>
        </SimpleFlexbox>
    );
};

_Md.propTypes = {
    componentLists: PropTypes.arrayOf(PropTypes.array),
    stateProps: PropTypes.object,
    experimentID: PropTypes.string
};

export { LayoutUnavailableResultsRow };
// exported for uni test
export { _Xs, _Md };
